import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { Table, Spin, DatePicker, message } from "antd";
import { useDasContributions } from "../../hooks/products/das/useDasContributions";
import { useDasPolicyStatementSummary } from "../../hooks/products/das/useDasPolicyStatementSummary";

// --- START: CustomPagination Component Definition (Required for UI) ---
function CustomPagination({ current, pageSize, total, onChange }) {
  const pageCount = Math.max(Math.ceil(total / pageSize), 1);
  const pages = Array.from({ length: pageCount }, (_, i) => i + 1);
  const navBtnStyle = {
    background: "transparent", color: "#184b18", border: "none", borderRadius: 0,
    padding: "0 10px", minWidth: 0, height: 26, fontWeight: 700, fontSize: 14,
    outline: "none", cursor: "pointer", transition: "color 0.2s", boxShadow: "none",
    margin: "0 2px", display: "flex", alignItems: "center", justifyContent: "center", opacity: 1,
  };
  const navBtnDisabledStyle = { ...navBtnStyle, color: "#bdbdbd", cursor: "not-allowed", opacity: 0.6 };
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <button onClick={() => current > 1 && onChange(current - 1)} disabled={current === 1}
        style={current === 1 ? navBtnDisabledStyle : navBtnStyle} aria-label="Previous page">{"<"}</button>
      {pages.map(page => (
        <button key={page} onClick={() => onChange(page)}
          style={{
            border: "0.5px solid #184b18",
            background: page === current ? "linear-gradient(90deg, #05b005d7 0%, #00b050 60%, #90ee90 100%)" : "transparent",
            color: page === current ? "#fff" : "#184b18", borderRadius: 18, padding: "0 14px",
            minWidth: 36, height: 36, fontWeight: 700, fontSize: 12, outline: "none",
            cursor: "pointer", transition: "background 0.2s, color 0.2s", boxShadow: "none", margin: "0 2px",
          }}>{page}</button>
      ))}
      <button onClick={() => current < pageCount && onChange(current + 1)} disabled={current === pageCount}
        style={current === pageCount ? navBtnDisabledStyle : navBtnStyle} aria-label="Next page">{">"}</button>
    </div>
  );
}
// --- END: CustomPagination Component Definition ---

function formatDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

export default function DasContributions() {
  const location = useLocation();
  const productDetails = location.state?.productDetails;
  const policyNumber = productDetails?.policyNumber;
  const memberNumber = productDetails?.memberNumber || productDetails?.memberId || "";

  const currentYear = new Date().getFullYear();
  const [filterFundYear, setFilterFundYear] = useState(null);
  const [filterDateFrom, setFilterDateFrom] = useState(null);
  const [filterDateTo, setFilterDateTo] = useState(null);
  const [filtersReady, setFiltersReady] = useState(false);

  useEffect(() => {
    if (policyNumber) setFiltersReady(true);
  }, [policyNumber]);

  const fundYears = Array.from({ length: 10 }, (_, i) => currentYear - i);

  const filters = {
    policyNumber,
    ...(memberNumber ? { memberNumber } : {}),
    ...(filterFundYear ? { fundYear: filterFundYear } : {}),
    ...(filterDateFrom ? { date_from: filterDateFrom.format("YYYY-MM-DD") } : {}),
    ...(filterDateTo ? { date_to: filterDateTo.format("YYYY-MM-DD") } : {}),
  };

  const { data: contributionsData, isLoading: contributionsLoading, isFetching: contributionsFetching } = useDasContributions(
    filters,
    !!policyNumber && filtersReady
  );

  // Generate statement (policy statement summary)
  const [isStatementGenerating, setIsStatementGenerating] = useState(false);
  const [statementEnabled, setStatementEnabled] = useState(false);
  const { data: statementData } = useDasPolicyStatementSummary(
    { policyNumber, statement_type: "summary", format: "pdf" },
    statementEnabled
  );

  useEffect(() => {
    if (statementEnabled && statementData) {
      const downloadUrl =
        statementData?.data?.download_url ||
        statementData?.data?.url ||
        statementData?.download_url ||
        statementData?.url;
      if (downloadUrl) {
        window.open(downloadUrl, "_blank");
        message.success("Statement generated successfully");
      } else if (statementData.success === false) {
        message.error(statementData.message || "Statement generation failed. Please try again.");
      } else {
        message.error("Statement generation failed. Please try again.");
      }
      setStatementEnabled(false);
      setIsStatementGenerating(false);
    }
  }, [statementEnabled, statementData]);

  const handleStatementDownload = () => {
    if (!policyNumber) {
      message.error("Policy number is missing.");
      return;
    }
    setIsStatementGenerating(true);
    setStatementEnabled(true);
  };

  const [showSearch, setShowSearch] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const searchInputRef = useRef(null);

  const extractContributions = (payload) => {
    if (!payload) return [];
    const raw = payload?.data;
    if (Array.isArray(raw)) return raw;
    if (Array.isArray(raw?.contributions)) return raw.contributions;
    if (Array.isArray(raw?.contribution_history)) return raw.contribution_history;
    if (Array.isArray(raw?.transaction_history?.transactions)) return raw.transaction_history.transactions;
    if (Array.isArray(payload?.contributions)) return payload.contributions;
    if (Array.isArray(payload)) return payload;
    return [];
  };

  const rawContributions = contributionsData ? extractContributions(contributionsData) : [];

  const normalized = rawContributions.map((item) => {
    const src = item || {};
    return {
      date: src.contribution_date || src.transaction_date || src.payment_date || src.date || "",
      fundYear: src.fund_year || src.fundYear || "",
      reference: src.reference || src.reference_no || src.transaction_ref || "",
      contributionType: src.contribution_type || src.type || src.description || "",
      amount: src.amount ?? src.contribution_amount ?? src.received ?? 0,
      interest: src.interest ?? src.interest_amount ?? 0,
      balance: src.balance ?? src.running_balance ?? null,
    };
  });

  const sorted = [...normalized].sort((a, b) => {
    const ta = new Date(a.date).getTime() || 0;
    const tb = new Date(b.date).getTime() || 0;
    return tb - ta;
  });

  const columns = [
    { title: "Date", dataIndex: "date", key: "date", align: "center",
      render: date => <div style={{ textAlign: "left", paddingLeft: 8, whiteSpace: "nowrap" }}>{formatDate(date)}</div> },
    { title: "Fund Year", dataIndex: "fundYear", key: "fundYear", align: "left" },
    { title: "Reference", dataIndex: "reference", key: "reference", align: "left", responsive: ["md"] },
    { title: "Type", dataIndex: "contributionType", key: "contributionType", align: "left" },
    { title: "Amount", dataIndex: "amount", key: "amount", align: "center",
      render: amount => <span style={{ color: "#111", fontWeight: 600, whiteSpace: "nowrap" }}>GHS {Math.abs(Number(amount) || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span> },
    { title: "Interest", dataIndex: "interest", key: "interest", align: "left",
      render: interest => <span style={{ whiteSpace: "nowrap" }}>GHS {Math.abs(Number(interest) || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>, responsive: ["sm"] },
    { title: "Balance", dataIndex: "balance", key: "balance", align: "left",
      render: balance => balance !== null && balance !== undefined
        ? <span style={{ whiteSpace: "nowrap" }}>GHS {Number(balance).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
        : "-", responsive: ["sm"] },
  ];

  let filtered = sorted;
  if (searchValue.trim()) {
    const val = searchValue.trim().toLowerCase();
    filtered = filtered.filter(t =>
      (t.reference || "").toLowerCase().includes(val) ||
      (t.contributionType || "").toLowerCase().includes(val) ||
      (t.amount || 0).toString().includes(val)
    );
  }

  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  const paginated = React.useMemo(() => {
    const startIdx = (currentPage - 1) * pageSize;
    return filtered.slice(startIdx, startIdx + pageSize);
  }, [filtered, currentPage, pageSize]);

  useEffect(() => { setCurrentPage(1); }, [filterFundYear, filterDateFrom, filterDateTo, searchValue]);

  function handleSearchClick() {
    setShowSearch(prev => !prev);
    setTimeout(() => {
      if (!showSearch && searchInputRef.current) searchInputRef.current.focus();
    }, 200);
  }

  const dateFormat = "YYYY-MM-DD";

  return (
    <div>
      <div className="container p-0 transactions-page">
        <div className="py-4 d-flex flex-column align-items-center">
          <div className="mb-3 w-100" style={{ maxWidth: 1330, paddingLeft: 12, paddingRight: 12 }}>
            <div className="d-flex flex-wrap align-items-center justify-content-between" style={{ gap: 16 }}>
              <div className="mb-2 mb-sm-0">
                <div className="page-title">DAS Contributions</div>
              </div>

              <div className="d-flex flex-wrap align-items-center controls-container" style={{ gap: 12 }}>
                <div className="search-container" style={{ position: "relative", display: "flex", alignItems: "center", borderRadius: 24, background: "#fff", boxShadow: "0 2px 8px 0 rgba(0,0,0,0.08)", padding: "4px 4px 4px 12px", transition: "all .28s ease", width: showSearch ? 280 : 120, minWidth: 120 }}>
                  <i className="bi bi-search" style={{ fontSize: 18, color: "#666", cursor: "pointer", zIndex: 2, marginRight: 8, flexShrink: 0 }}
                    aria-label="Search" onClick={handleSearchClick} tabIndex={0}
                    onKeyDown={e => { if (e.key === "Enter" || e.key === " ") handleSearchClick(); }} role="button"></i>
                  <input ref={searchInputRef} type="text" placeholder={showSearch ? "Search by ref/type/amount..." : "Search"}
                    className="form-control border-0"
                    style={{ background: "transparent", border: "none", boxShadow: "none", textAlign: "left", padding: "8px 0", width: showSearch ? 200 : 60, opacity: 1, transition: "width .28s ease" }}
                    value={searchValue} onChange={e => setSearchValue(e.target.value)}
                    onBlur={() => { if (!searchValue) setShowSearch(false); }} />
                </div>

                <div className="controls-left d-flex align-items-center" style={{ gap: 12 }}>
                  <omk-select label="Fund Year" value={filterFundYear || ""} disabled={!policyNumber}
                    onInput={(e) => setFilterFundYear(e.target.value ? Number(e.target.value) : null)}>
                    <omk-select-option value="">All years</omk-select-option>
                    {fundYears.map(y => <omk-select-option key={y} value={y}>{y}</omk-select-option>)}
                  </omk-select>
                  <DatePicker value={filterDateFrom} onChange={setFilterDateFrom} format={dateFormat} placeholder="Date from" disabled={!policyNumber} style={{ borderRadius: 24, height: 40 }} />
                  <DatePicker value={filterDateTo} onChange={setFilterDateTo} format={dateFormat} placeholder="Date to" disabled={!policyNumber} style={{ borderRadius: 24, height: 40 }} />
                </div>

                <div className="controls-right d-flex align-items-center">
                  <button className="btn btn-primary generate-btn" onClick={handleStatementDownload} disabled={isStatementGenerating}>
                    {isStatementGenerating ? <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}><Spin size="small" /> Generating...</span> : "Generate Statement"}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <style>{`
            .transactions-page .page-title { font-weight:700; color:#111; font-size: clamp(18px, 2.2vw, 22px); }
            .transactions-page .search-container { border-radius: 24px; background: #fff; box-shadow: 0 2px 8px 0 rgba(0,0,0,0.08); overflow: hidden; }
            .transactions-page .generate-btn { border-radius: 24px; height: 36px; font-weight: 600; min-width: 120px; }
            .transactions-page .card { width: 97%; max-width: 1330px; min-width: 0; }
            .transactions-page .ant-table-wrapper { overflow-x: auto; }
            .transactions-page .ant-table-body { overflow-x: auto !important; }
            .transactions-page .ant-table tbody td { font-size: clamp(12px, 1.8vw, 14px); }
            .transactions-page .ant-table thead th { font-size: clamp(12px, 1.8vw, 13px); }
            .transactions-page .ant-picker { width: 130px; }
            @media (max-width: 900px) {
              .transactions-page .generate-btn { min-width: 96px; padding: 8px 12px; }
              .transactions-page .search-container { width: 160px !important; min-width: 160px !important; }
            }
            @media (max-width: 576px) {
              .transactions-page { padding: 8px; }
              .transactions-page .page-title { font-size: 16px; margin-bottom: 12px; }
              .transactions-page .controls-container { width: 100%; flex-direction: column; align-items: stretch; gap: 12px; }
              .transactions-page .controls-left { width: 100%; flex-direction: column; align-items: stretch; gap: 12px; }
              .transactions-page .controls-left > div { width: 100%; }
              .transactions-page .controls-left omk-select { width: 100% !important; min-width: 100% !important; }
              .transactions-page .controls-left .ant-picker { width: 100% !important; }
              .transactions-page .controls-right { width: 100%; }
              .transactions-page .controls-right .generate-btn { width: 100%; }
              .transactions-page .search-container { width: 100% !important; min-width: 100% !important; padding: 8px 12px 8px 12px !important; }
              .transactions-page .search-container input { width: 100% !important; }
              .transactions-page .generate-btn { min-width: unset; padding: 10px 16px; font-size: 13px; height: 40px; }
              .transactions-page .card { width: 100%; min-width: 0; border-radius: 10px; }
              .transactions-page .ant-table { font-size: 11px; }
              .transactions-page .ant-table table { min-width: max-content; }
              .transactions-page .ant-table thead th, .transactions-page .ant-table tbody td { padding: 8px 4px; font-size: 11px; }
              .transactions-page .d-flex.justify-content-between.align-items-center.px-4.py-3 { flex-direction: column; gap: 12px; padding: 12px; }
            }
            @media (max-width: 480px) {
              .transactions-page .page-title { font-size: 15px; }
              .transactions-page .search-container { padding: 6px 10px 6px 10px !important; }
              .transactions-page .ant-table { font-size: 10px; }
              .transactions-page .ant-table thead th, .transactions-page .ant-table tbody td { padding: 6px 2px; font-size: 10px; }
            }
          `}</style>

          <div className="w-100 d-flex justify-content-center" style={{ minHeight: 120, background: "transparent" }}>
            <div className="card p-0 shadow-sm" style={{ width: "97%", maxWidth: 1330, minWidth: 320, color: "#111", overflow: "auto", borderRadius: 10, position: "relative" }}>
              {(contributionsLoading || contributionsFetching) && (
                <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(255,255,255,0.6)", zIndex: 20 }}>
                  <Spin size="large" />
                </div>
              )}
              <Table
                columns={columns}
                dataSource={paginated}
                rowKey={(record, idx) => idx}
                pagination={false}
                locale={{ emptyText: <span className="text-muted" style={{ fontSize: 18 }}>No contributions found.</span> }}
                style={{ overflow: "hidden", borderRadius: 24 }}
                scroll={{ x: "max-content" }}
              />
            </div>
          </div>

          <div className="w-100 d-flex justify-content-center mt-2">
            <div className="d-flex justify-content-between align-items-center px-4 py-3" style={{ maxWidth: 1330, minWidth: 0, width: "100%", borderRadius: 10, border: "none", background: "transparent" }}>
              <div className="text-muted d-none d-sm-block" style={{ fontSize: 15, fontWeight: 500 }}>
                Showing 1-{paginated.length} of {filtered.length} results
              </div>
              <CustomPagination current={currentPage} pageSize={pageSize} total={filtered.length} onChange={setCurrentPage} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
